import type { ProductStatus } from "@adpilot/shared";
import { IconPhoto } from "@tabler/icons-react";
import { cva } from "class-variance-authority";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/preview-card";
import { useProductPreviewStatuses } from "@/hooks/use-product-preview-statuses";
import { cn } from "@/lib/utils";

const thumbnailVariants = cva(
  "grid shrink-0 place-items-center overflow-hidden rounded-md border border-line bg-surface p-1",
  { variants: { size: { default: "size-12", compact: "size-9", context: "size-10" } } },
);

export function ProductAdvertisingStatusBadge({ status }: { status: ProductStatus }) {
  const { t } = useTranslation("campaignCreation");
  const variant =
    status === "advertised" ? "success" : status === "unavailable" ? "warning" : "neutral";
  return (
    <Badge variant={variant} size="compact">
      {t(`products.status.${status}`)}
    </Badge>
  );
}

export function ProductThumbnail({
  imageUrl,
  label,
  asin,
  sku,
  productStatus,
  compact = false,
  size,
  thumbnail: customThumbnail,
}: {
  thumbnail?: ReactNode;
  imageUrl: string | null;
  label: string;
  asin?: string | null;
  sku?: string;
  productStatus?: ProductStatus;
  compact?: boolean;
  size?: "default" | "compact" | "context";
}) {
  const { t } = useTranslation("campaignCreation");
  const thumbnail = (
    <span className={cn(thumbnailVariants({ size: size ?? (compact ? "compact" : "default") }))}>
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={label}
          className="size-full object-contain"
          loading="lazy"
          draggable={false}
        />
      ) : (
        <IconPhoto className="size-5 text-muted-foreground/55" aria-hidden="true" />
      )}
    </span>
  );

  return (
    <HoverCard>
      <HoverCardTrigger
        delay={300}
        closeDelay={80}
        render={
          <button
            type="button"
            aria-label={t("products.previewImage", { product: label })}
            className="block shrink-0 rounded-md outline-none ring-ring/15 focus-visible:ring-2"
            onClick={(event) => event.stopPropagation()}
          />
        }
      >
        {customThumbnail ?? thumbnail}
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        side="top"
        sideOffset={8}
        className="w-[360px] max-w-[calc(100vw-2rem)] flex-col gap-4 text-left [text-wrap:wrap]"
      >
        <div className="flex items-start gap-4">
          <span className="grid size-20 shrink-0 place-items-center overflow-hidden rounded-md border border-line bg-surface p-2">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt=""
                className="size-full object-contain"
                loading="lazy"
                draggable={false}
              />
            ) : (
              <IconPhoto className="size-8 text-muted-foreground/45" aria-hidden="true" />
            )}
          </span>
          <p className="min-w-0 flex-1 break-words text-sm font-semibold leading-5">{label}</p>
        </div>
        <dl className="flex flex-col gap-2 border-t border-line pt-3 text-xs leading-[18px]">
          {asin ? (
            <div className="flex gap-3">
              <dt className="w-20 shrink-0 text-muted-foreground">ASIN</dt>
              <dd className="min-w-0 break-all font-mono">{asin}</dd>
            </div>
          ) : null}
          {sku ? (
            <div className="flex gap-3">
              <dt className="w-20 shrink-0 text-muted-foreground">SKU</dt>
              <dd className="min-w-0 break-all font-mono">{sku}</dd>
            </div>
          ) : null}
          <div className="flex items-start gap-3">
            <dt className="w-20 shrink-0 text-muted-foreground">
              {t("products.table.advertisingStatus")}
            </dt>
            <dd>
              {/* Unknown (still resolving or not found) is not the same as unavailable. */}
              {productStatus ? <ProductAdvertisingStatusBadge status={productStatus} /> : "—"}
            </dd>
          </div>
        </dl>
      </HoverCardContent>
    </HoverCard>
  );
}

interface ProductPreviewIdentity {
  sku: string;
  asin: string;
  title: string | null;
  imageUrl: string | null;
}

export function ProductThumbnailWithStatus({
  product,
  shopId,
  hideSku = false,
}: {
  product: ProductPreviewIdentity;
  shopId: string;
  /** A vendor's `sku` is its ASIN (#674): keep it as the status key, never show it as a SKU. */
  hideSku?: boolean;
}) {
  const statuses = useProductPreviewStatuses(shopId, [product]);
  return (
    <ProductThumbnail
      imageUrl={product.imageUrl}
      label={product.title ?? product.asin}
      asin={product.asin}
      sku={hideSku ? undefined : product.sku}
      productStatus={statuses.get(product.sku)}
      size="context"
    />
  );
}
